import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';

import CustomHeader from './CustomHeader';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { useToast } from 'context/ToastContext';

import { useFileMutations } from 'modules/FileManagerV2/hooks/api/useFileMutations';
import { toastConstant } from 'constants/toastmessage';
import { queryKeys } from 'hooks/config/queryClient';

function AddTagModal() {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useForm();
  const { modals, caseDetails, isGlobalFileManager, selectedItem, globalTags, getGlobalTags, fileManagerFilters, isLeadFileManager } =
    useFileManagerContext();
  const { showAddTag, setShowAddTag, tagModalType } = modals;
  const { addToast } = useToast();
  const { saveTags } = useFileMutations();
  const [showGlobalTags, setShowGlobalTags] = useState(false);
  const queryClient = useQueryClient();
  const tagRef = useRef();

  const isAddTagMode = tagModalType === 'add';
  const isEditTagMode = tagModalType === 'edit';

  useEffect(() => {
    getGlobalTags();
    if (isEditTagMode) {
      setValue('tags', selectedItem.tags);
    }
  }, []);

  function submitTagHandler(data) {
    const inputValue = tagRef?.current?.inputRef?.current?.value;

    if (!data?.tags?.length && !inputValue) {
      setError('tags');
      return;
    }
    if (inputValue) {
      data.tags.push(inputValue);
    }

    const arr = data?.tags?.map((val) => val.replace(/\s+/g, ''));
    changeTags(arr);
  }

  const changeTags = async (tags) => {
    let obj = {
      data: { pk: selectedItem.pk, sk: selectedItem.sk, tags },
      is_lead: isLeadFileManager ? true : false,
      caseIdForInvalidation: isGlobalFileManager ? fileManagerFilters?.case?.id : caseDetails?.case_id,
    };

    try {
      await saveTags(obj);
      queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() });
      setShowAddTag(false);
      let msg = isAddTagMode ? toastConstant.message.TAGS_ADDED_SUCCESS : toastConstant.message.TAGS_UPDATED_SUCCESS;
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, msg);
    } catch (error) {
      console.log(error);
      let msg = isAddTagMode ? toastConstant.message.TAGS_ADDED_FAILED : toastConstant.message.TAGS_UPDATED_FAILED;
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
    }
  };

  const TagFooter = () => (
    <div className="footer-btn">
      <Button label="Cancel" onClick={() => setShowAddTag(false)} className="p-button-secondary outline" />
      <Button label={isAddTagMode ? 'Save' : 'Update'} onClick={handleSubmit(submitTagHandler)} className="p-button-secondary" />
    </div>
  );

  const chipTemplate = (e) => {
    if (e) {
      try {
        let formattedTagData = e?.replaceAll('#', '');
        return <div>{'#' + formattedTagData}</div>;
      } catch (error) {
        return <div>{e}</div>;
      }
    }
  };

  const handleTagsChange = (e) => {
    let arr = [];
    if (e?.value?.length) {
      arr = e.value.map((val) => val.replace(/\s+/g, ''));
    }
    setValue('tags', arr || e.value);
  };

  return (
    <Dialog
      header={<CustomHeader onHide={() => setShowAddTag(false)} />}
      footer={<TagFooter />}
      visible={showAddTag}
      className="new-task-popup"
      onHide={() => setShowAddTag(false)}
      modal
      closable={false}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">
          {isAddTagMode ? 'Add Tag' : 'Update Tag'} <span className="text-danger">{isEditTagMode ? '*' : ''}</span>
        </div>
      </div>

      <Controller
        name="tags"
        control={control}
        defaultValue={[]}
        render={({ field }) => (
          <Chips
            itemTemplate={chipTemplate}
            placeholder="Press enter to add a tag"
            id={field.name}
            value={field.value}
            ref={tagRef}
            onChange={handleTagsChange}
            className="border-0 w-100 taging update-tag add-tag-chips-input mb-0"
          />
        )}
      />
      {errors?.tags && <InputErrorMessage>Required</InputErrorMessage>}

      <div className="col-12">
        <div className="row mb-2">
          <div className="col-sm-12 addfield">
            <Button
              type="button"
              className="p-button button-text text-medium addfield ps-0"
              onClick={(e) => {
                e.preventDefault();
                setShowGlobalTags(!showGlobalTags);
              }}
            >
              <i className="icon-add F-size14 me-1"></i>Add Global Tags
            </Button>
          </div>
        </div>

        {showGlobalTags ? (
          globalTags?.length > 0 ? (
            <div className="row mb-3">
              <div className="col-12">
                <div className="global-tag-wrapper input-shadow" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {globalTags.map((element, index) => {
                    let hashReplacedTag = element.value.replace('#', '');
                    return (
                      <span
                        key={index}
                        className={`tag-highlight ${watch('tags')?.includes(hashReplacedTag) ? 'selected-global-tag' : ''}`}
                        onClick={(e) => {
                          let existingTags = watch('tags') ? watch('tags') : [];
                          existingTags.push(hashReplacedTag);
                          setValue('tags', [...new Set(existingTags)]);
                        }}
                      >
                        {element.value}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="row mb-2">
              <div className="col-12 small">
                <i className="fas fa-info-circle mx-2 mt-1"></i>
                <span className="text-hint">No global tags found.</span>{' '}
              </div>
            </div>
          )
        ) : null}
      </div>
    </Dialog>
  );
}

export default AddTagModal;
